import { createColumnHelper } from "@tanstack/react-table";
import { useGetNotificationLogs, useGetNotificationLogStats } from "../../queries/notifications";
import { useMemo, useState, useEffect } from "react";
import TanStackTable from "../../components/TanStackTable";
import { ActionButton } from "../../components/Forms/ActionButton";
import Modal from "../Submissions/components/Modal";
import 'react-toastify/dist/ReactToastify.css';
import { toast, ToastContainer } from "react-toastify";
import ActiveStatus from "../../components/Common/ActiveStatus";
import { FaEye } from "react-icons/fa";

const NotificationLogs = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedLog, setSelectedLog] = useState(null);

    // Filter states
    const [filters, setFilters] = useState({
        status: '',
        type: '',
        channel: '',
        userId: '',
        email: '',
        fromDate: '',
        toDate: '',
        sortBy: 'createdAt',
        sortOrder: 'desc'
    });

    const { data: logsData, isPending, isFetching } = useGetNotificationLogs(filters, currentPage, pageSize);
    const { data: statsData } = useGetNotificationLogStats();

    const logs = logsData?.data || [];
    const pagination = logsData?.pagination || {};
    const filterOptions = logsData?.filters || {};

    // Handle page change
    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    // Handle page size change
    const handlePageSizeChange = (size) => {
        setPageSize(size);
        setCurrentPage(1);
    };

    // Handle filter change
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setCurrentPage(1); // Reset to first page when filters change
    };

    // Handle view details
    const handleViewDetails = (log) => {
        setSelectedLog(log);
        setShowDetailsModal(true);
    };

    const columnHelper = createColumnHelper();

    const columns = useMemo(() => [
        columnHelper.display({
            id: 'srno',
            header: 'Sr No.',
            cell: info => (currentPage - 1) * pageSize + info.row.index + 1,
        }),
        columnHelper.accessor('userId', {
            header: 'User',
            cell: info => {
                const user = info.getValue();
                return user ? `${user.name} (${user.email})` : 'N/A';
            },
        }),
        columnHelper.accessor('email', {
            header: 'Email',
            cell: info => info.getValue() || 'N/A',
        }),
        columnHelper.accessor('type', {
            header: 'Type',
            cell: info => info.getValue() || 'N/A',
        }),
        columnHelper.accessor('channel', {
            header: 'Channel',
            cell: info => info.getValue() || 'N/A',
        }),
        columnHelper.accessor('status', {
            header: 'Status',
            cell: info => {
                const status = info.getValue();
                const statusColors = {
                    sent: 'text-green-600',
                    failed: 'text-red-600',
                    queued: 'text-yellow-600',
                    sending: 'text-blue-600',
                    retrying: 'text-orange-600'
                };
                return (
                    <span className={`capitalize ${statusColors[status] || 'text-gray-600'}`}>
                        {status}
                    </span>
                );
            },
        }),
        columnHelper.accessor('attempts', {
            header: 'Attempts',
            cell: info => `${info.getValue()}/${info.row.original.maxAttempts}`,
        }),
        columnHelper.accessor('createdAt', {
            header: 'Created At',
            cell: info => (
                <div className='text-center'>
                    {new Date(info.getValue()).toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' })}
                    <br />
                    <small className='text-secondary'>
                        {new Date(info.getValue()).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                    </small>
                </div>
            ),
        }),
        columnHelper.accessor('actions', {
            header: 'Actions',
            align: 'center',
            enableColumnFilter: false,
            cell: info => {
                const log = info.row.original;
                return (
                    <div className='flex items-center justify-center gap-2'>
                        <button
                            className="rounded p-1.5 bg-[rgba(52,199,89,0.1)] hover:bg-[rgba(52,199,89,0.2)] mx-1 min-h-7 min-w-7"
                            onClick={() => handleViewDetails(log)}
                            title="View Details"
                        >
                            <FaEye className="fill-primary" />
                        </button>
                    </div>
                );
            },
        }),
    ], [currentPage, pageSize]);

    if (isPending && !logsData) {
        return <div>Loading notification logs...</div>;
    }

    return (
        <main className="flex flex-col text-base font-semibold text-gray-900 rounded-none">
            {/* Stats Cards */}
            {statsData?.data && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    {statsData.data.map((stat, index) => (
                        <div key={index} className="bg-white p-4 rounded-lg shadow-md">
                            <h3 className="text-lg font-bold text-gray-800 capitalize">
                                {stat._id || 'Total'}
                            </h3>
                            <p className="text-2xl font-bold text-primary">
                                {stat.total || 0}
                            </p>
                            <div className="mt-2">
                                {stat.channels?.map((channel, idx) => (
                                    <div key={idx} className="text-sm text-gray-600">
                                        {channel.channel}: {channel.count}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Filters */}
            <form className="flex flex-col p-5 bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] mb-5">
                <div className="flex flex-wrap gap-4">
                    <div className='flex gap-3 w-full'>
                        <div className="flex-col w-full md:w-1/4">
                            <label htmlFor="status" className="gap-1 self-stretch w-full text-gray-900 text-base">
                                Status
                            </label>
                            <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <select
                                    name="status"
                                    value={filters.status}
                                    onChange={(e) => handleFilterChange('status', e.target.value)}
                                    className="flex-1 shrink self-stretch my-auto basis-0 bg-transparent border-none outline-none text-base"
                                >
                                    <option value="">All</option>
                                    {filterOptions.statuses?.map(status => (
                                        <option key={status} value={status}>{status}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="flex-col w-full md:w-1/4">
                            <label htmlFor="type" className="gap-1 self-stretch w-full text-gray-900 text-base">
                                Type
                            </label>
                            <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <select
                                    name="type"
                                    value={filters.type}
                                    onChange={(e) => handleFilterChange('type', e.target.value)}
                                    className="flex-1 shrink self-stretch my-auto basis-0 bg-transparent border-none outline-none text-base"
                                >
                                    <option value="">All</option>
                                    {filterOptions.types?.map(type => (
                                        <option key={type} value={type}>{type}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="flex-col w-full md:w-1/4">
                            <label htmlFor="channel" className="gap-1 self-stretch w-full text-gray-900 text-base">
                                Channel
                            </label>
                            <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <select
                                    name="channel"
                                    value={filters.channel}
                                    onChange={(e) => handleFilterChange('channel', e.target.value)}
                                    className="flex-1 shrink self-stretch my-auto basis-0 bg-transparent border-none outline-none text-base"
                                >
                                    <option value="">All</option>
                                    {filterOptions.channels?.map(channel => (
                                        <option key={channel} value={channel}>{channel}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="flex-col w-full md:w-1/4">
                            <label htmlFor="userId" className="gap-1 self-stretch w-full text-gray-900 text-base">
                                User ID
                            </label>
                            <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <input
                                    type="text"
                                    name="userId"
                                    value={filters.userId}
                                    onChange={(e) => handleFilterChange('userId', e.target.value)}
                                    placeholder="Enter User ID..."
                                    className='outline-none focus:ring-0 w-full'
                                />
                            </div>
                        </div>
                    </div>
                    <div className='flex gap-3 w-full'>
                        <div className="flex-col w-full md:w-1/4">
                            <label htmlFor="email" className="gap-1 self-stretch w-full text-gray-900 text-base">
                                Email
                            </label>
                            <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <input
                                    type="text"
                                    name="email"
                                    value={filters.email}
                                    onChange={(e) => handleFilterChange('email', e.target.value)}
                                    placeholder="Enter Email..."
                                    className='outline-none focus:ring-0 w-full'
                                />
                            </div>
                        </div>
                        <div className="flex-col w-full md:w-1/4">
                            <label htmlFor="fromDate" className="gap-1 self-stretch w-full text-gray-900 text-base">
                                From Date
                            </label>
                            <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <input
                                    type="date"
                                    name="fromDate"
                                    value={filters.fromDate}
                                    onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="flex-col w-full md:w-1/4">
                            <label htmlFor="toDate" className="gap-1 self-stretch w-full text-gray-900 text-base">
                                To Date
                            </label>
                            <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <input
                                    type="date"
                                    name="toDate"
                                    value={filters.toDate}
                                    onChange={(e) => handleFilterChange('toDate', e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="flex-col w-full md:w-1/4">
                            <label htmlFor="sortBy" className="gap-1 self-stretch w-full text-gray-900 text-base">
                                Sort By
                            </label>
                            <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <select
                                    name="sortBy"
                                    value={filters.sortBy}
                                    onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                                    className="flex-1 shrink self-stretch my-auto basis-0 bg-transparent border-none outline-none text-base"
                                >
                                    <option value="createdAt">Created At</option>
                                    <option value="type">Type</option>
                                    <option value="status">Status</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
                <div className='flex gap-3 items-center justify-end mt-4'>
                    <ActionButton
                        variant={'reset'}
                        text={'Reset'}
                        handleSubmit={() => {
                            setFilters({
                                status: '',
                                type: '',
                                channel: '',
                                userId: '',
                                email: '',
                                fromDate: '',
                                toDate: '',
                                sortBy: 'createdAt',
                                sortOrder: 'desc'
                            });
                            setCurrentPage(1);
                        }}
                    />
                </div>
            </form>

            {/* Table */}
            <section className="flex flex-col pt-5 w-full mt-5 bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] max-md:max-w-full">
                <div className="flex flex-col pr-px w-full pl-6 max-md:max-w-full">
                    <div className="flex items-center justify-between pb-4">
                        <h1 className="self-start text-lg font-bold">Notification Logs</h1>
                    </div>
                    {logs && logs.length > 0 ? (
                        <TanStackTable
                            data={logs}
                            columns={columns}
                            enablePagination={true}
                            manualPagination={true}
                            pageCount={pagination.totalPages || 1}
                            currentPage={currentPage}
                            pageSize={pageSize}
                            totalItems={pagination.totalItems || 0}
                            onPageChange={handlePageChange}
                            onPageSizeChange={handlePageSizeChange}
                            isLoading={isFetching}
                        />
                    ) : (
                        <div className="pb-4">
                            No notification logs available
                        </div>
                    )}
                </div>
            </section>

            {/* Details Modal */}
            <Modal isOpen={showDetailsModal} onClose={() => setShowDetailsModal(false)} title="Notification Log Details">
                <div className="flex flex-col gap-4 max-h-96 overflow-y-auto">
                    {selectedLog && (
                        <>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="font-semibold">User:</label>
                                    <p>{selectedLog.userId ? `${selectedLog.userId.name} (${selectedLog.userId.email})` : 'N/A'}</p>
                                </div>
                                <div>
                                    <label className="font-semibold">Email:</label>
                                    <p>{selectedLog.email || 'N/A'}</p>
                                </div>
                                <div>
                                    <label className="font-semibold">Type:</label>
                                    <p>{selectedLog.type || 'N/A'}</p>
                                </div>
                                <div>
                                    <label className="font-semibold">Channel:</label>
                                    <p>{selectedLog.channel || 'N/A'}</p>
                                </div>
                                <div>
                                    <label className="font-semibold">Status:</label>
                                    <p className="capitalize">{selectedLog.status || 'N/A'}</p>
                                </div>
                                <div>
                                    <label className="font-semibold">Attempts:</label>
                                    <p>{selectedLog.attempts}/{selectedLog.maxAttempts}</p>
                                </div>
                                <div>
                                    <label className="font-semibold">Created At:</label>
                                    <p>{new Date(selectedLog.createdAt).toLocaleString()}</p>
                                </div>
                                <div>
                                    <label className="font-semibold">Queued At:</label>
                                    <p>{selectedLog.queuedAt ? new Date(selectedLog.queuedAt).toLocaleString() : 'N/A'}</p>
                                </div>
                                {selectedLog.sentAt && (
                                    <div>
                                        <label className="font-semibold">Sent At:</label>
                                        <p>{new Date(selectedLog.sentAt).toLocaleString()}</p>
                                    </div>
                                )}
                                {selectedLog.failedAt && (
                                    <div>
                                        <label className="font-semibold">Failed At:</label>
                                        <p>{new Date(selectedLog.failedAt).toLocaleString()}</p>
                                    </div>
                                )}
                            </div>
                            {selectedLog.subject && (
                                <div>
                                    <label className="font-semibold">Subject:</label>
                                    <p>{selectedLog.subject}</p>
                                </div>
                            )}
                            {selectedLog.error && (
                                <div>
                                    <label className="font-semibold">Error:</label>
                                    <pre className="bg-gray-100 p-2 rounded text-sm overflow-x-auto">
                                        {JSON.stringify(selectedLog.error, null, 2)}
                                    </pre>
                                </div>
                            )}
                            {selectedLog.payload && (
                                <div>
                                    <label className="font-semibold">Payload:</label>
                                    <pre className="bg-gray-100 p-2 rounded text-sm overflow-x-auto">
                                        {JSON.stringify(selectedLog.payload, null, 2)}
                                    </pre>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </Modal>

            <ToastContainer
                position="top-right"
                autoClose={2000}
            />
        </main>
    );
}

export default NotificationLogs;